import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api } from "@/lib/api";

export default function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Name, email, and message are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setSent(true);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      toast.success("Message sent — we'll be in touch.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed — try again");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="brut pcs-bg-cream p-6 text-center" data-testid="contact-form-sent">
        <div className="font-display text-3xl tracking-tight mb-2">Thanks!</div>
        <p className="text-sm">
          We've got your message. Expect a reply within a few days — and longer in October.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-4 font-mono uppercase text-[10px] tracking-widest underline"
          data-testid="contact-form-reset"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="brut bg-white p-5 md:p-6 space-y-4"
      data-testid="contact-form"
    >
      <div className="font-display text-2xl tracking-tight">Send us a message</div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Your name *
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="contact-name"
            className="w-full brut bg-white px-3 py-2 focus:outline-none"
          />
        </div>
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Email *
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="contact-email"
            className="w-full brut bg-white px-3 py-2 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Subject
        </label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="(Optional)"
          data-testid="contact-subject"
          className="w-full brut bg-white px-3 py-2 focus:outline-none"
        />
      </div>

      <div>
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Message *
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          rows={6}
          maxLength={5000}
          data-testid="contact-message"
          className="w-full brut bg-white px-3 py-2 focus:outline-none resize-y"
        />
        <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 text-right mt-1">
          {message.length} / 5000
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="contact-submit"
          className="brut pcs-bg-pink text-white px-5 py-2.5 font-bold uppercase text-sm tracking-wide brut-press disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Send className="w-4 h-4" strokeWidth={3} />
          {busy ? "Sending..." : "Send message"}
        </button>
      </div>
    </form>
  );
}
